import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

const API ="https://simplix.co.za/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Circuit breaker
  const refreshFailCount = useRef(0);
  const MAX_REFRESH_RETRIES = 3;

  const clearSession = useCallback(() => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("profile");
    setToken(null);
    setProfile(null);
    setLoading(false);
  }, []);

  // ─── Refresh: just POST to /api/auth/refresh/ with credentials ───────
  // The browser auto-sends the HttpOnly "p4r" cookie. No body needed.
  const refreshAccessToken = useCallback(async () => {
    if (refreshFailCount.current >= MAX_REFRESH_RETRIES) {
      console.warn("[Auth] Max refresh retries — clearing session.");
      clearSession();
      return null;
    }

    try {
      const resp = await fetch(`${API}/auth/refresh/`, {
        method: "POST",
        credentials: "include", // ← sends the cookie
      });

      if (!resp.ok) {
        refreshFailCount.current += 1;
        if (refreshFailCount.current >= MAX_REFRESH_RETRIES) {
          setError("Your session has expired. Please log in again.");
          clearSession();
        }
        return null;
      }

      const data = await resp.json();
      refreshFailCount.current = 0; // reset on success

      if (data.access) {
        setToken(data.access);
        localStorage.setItem("auth_token", data.access);
      }
      return data.access;
    } catch (err) {
      refreshFailCount.current += 1;
      console.error("[Auth] Refresh error:", err.message);
      if (refreshFailCount.current >= MAX_REFRESH_RETRIES) {
        setError("Could not refresh your session. Please log in again.");
        clearSession();
      }
      return null;
    }
  }, [clearSession]);

  // ─── Restore session on mount ─────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        // Try refreshing via cookie
        const res = await fetch(`${API}/auth/refresh/`, {
          method: "POST",
          credentials: "include",
        });

        if (!res.ok) {
          setLoading(false);
          return;
        }

        const data = await res.json();
        if (data.access) {
          setToken(data.access);
          localStorage.setItem("auth_token", data.access);

          // Fetch profile
          try {
            const profRes = await fetch(`${API}/auth/me/`, {
              headers: { Authorization: `Bearer ${data.access}` },
              credentials: "include",
            });
            if (profRes.ok) {
              const profData = await profRes.json();
              setProfile(profData.profile);
              localStorage.setItem("profile", JSON.stringify(profData.profile));
            }
          } catch (e) {
            console.error("Failed to fetch profile:", e);
          }
        }
      } catch (e) {
        console.error("Session restore failed:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ─── Login ─────────────────────────────────────────────────────────────
  const login = async (email, password, captchaToken) => {
    const body = {
      email: email.trim().toLowerCase(),
      password,
    };
    if (captchaToken) body.captcha_token = captchaToken;

    const res = await fetch(`${API}/auth/login/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", // ← so the Set-Cookie from response is stored
      body: JSON.stringify(body),
    });

    // Handle 429 with captcha_required flag
    if (res.status === 429) {
      const j = await res.json().catch(() => ({}));
      if (j.captcha_required) {
        const err = new Error("CAPTCHA required");
        err.captchaRequired = true;
        throw err;
      }
      throw new Error(j.message || "Too many attempts. Try again later.");
    }

    if (res.status === 401) {
      const j = await res.json().catch(() => ({}));
      if (j.account_locked) {
        throw new Error(j.message || "Account temporarily locked.");
      }
      throw new Error("Incorrect email or password.");
    }

    if (res.status === 423) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.message || "Account temporarily locked.");
    }

    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.detail || j.message || "Login failed");
    }

    const j = await res.json();
    setToken(j.token);
    setProfile(j.profile);
    localStorage.setItem("auth_token", j.token);
    localStorage.setItem("profile", JSON.stringify(j.profile));

    // NOTE: refresh token is in the HttpOnly cookie — nothing to do here.
    // The browser stored it from the Set-Cookie header automatically.

    return j.profile;
  };

  // ─── Logout ────────────────────────────────────────────────────────────
  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout/`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      // ignore
    } finally {
      clearSession();
      // Clear the cookie server-side sets it; client-side we can delete
      // it too for immediate effect (belt-and-suspenders):
      document.cookie = "p4r=; path=/api/; max-age=0";
    }
  };

  return (
    <AuthContext.Provider
      value={{ token, profile, loading, error, login, logout, refreshAccessToken }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}